// "SNF" is the user-facing label for predecessor facilities regardless of whether the
// underlying data includes SNF, LTC, or both levels of care. To our users "SNF" is
// synonymous with "facility" or "building" — it's the familiar term for where patients
// are coming from, even when LTC residents are included in the data.
export const PREDECESSOR_LOCATION_LABEL = 'SNF';

export enum Routes {
  INSIGHTS_BASE = '/insights',
  OLIO_ENGAGEMENT_SCORE = 'olio-engagement-score',
  AVERAGE_LENGTH_OF_STAY = 'average-length-of-stay',
  READMISSION_RATE = 'readmission-rate',
  TOTAL_DISCHARGES = 'total-discharges',
  PRIORITIES_NOTE_RESPONSE = 'priorities',
  ESCALATIONS_RESPONSE = 'escalations',
  ACTIVITIES_PER_PATIENT_PER_WEEK = 'activities-per-patient-per-week',
}

export enum Metrics {
  ACCEPTANCE_RATE = 'acceptance_rate',
  ACCEPTED_COUNT = 'accepted_count',
  ACCEPTED_TO_DISCHARGE_DAYS_AVERAGE = 'accepted_to_discharge_days:average',
  ACTIVITIES_PER_PATIENT_PER_WEEK = 'activities_per_patient_per_week',
  BUSINESS_RESPONSE_TIME_AVERAGE = 'business_response_time:average',
  CANCEL_RATE = 'cancel_rate',
  CREATED_TO_DISCHARGE_DAYS_AVERAGE = 'created_to_discharge_days:average',
  DAYS_TO_START_CARE_AVERAGE = 'days_to_start_care:average',
  DENIAL_RATE = 'denial_rate',
  DENIED_COUNT = 'denied_count',
  ID_COUNT = 'id:count',
  LENGTH_OF_STAY_AVERAGE = 'length_of_stay:average',
  LENGTH_OF_STAY_SUM = 'length_of_stay:sum',
  LOCATION_EPISODE_ID_COUNT = 'location_episode_id:count',
  NOT_SELECTED_COUNT = 'not_selected_count',
  NOT_SELECTED_RATE = 'not_selected_rate',
  OLIO_ENGAGEMENT_SCORE = 'olio_engagement_score',
  OWNER_ACTIVITIES_SUM = 'owner_activities:sum',
  PENDING_COUNT = 'pending_count',
  PENDING_RATE = 'pending_rate',
  READMISSION_LENGTH_OF_STAY_AVERAGE = 'readmission_length_of_stay:average',
  READMISSION_RATE = 'readmission_rate',
  READMISSIONS_SUM = 'readmissions:sum',
  RESPONSE_TIME_AVERAGE = 'response_time:average',
  RESPONSE_TIME_SECONDS_AVERAGE = 'response_time_seconds:average',
  REFERRAL_COUNT = 'referral:count',
  REVIEW_ID_COUNT = 'review_id:count',
  SAFE_RETURNS_REQUESTED_COUNT = 'safe_returns_requested:count',
  SAFE_RETURN_RESPONSE_TIME_AVERAGE = 'safe_return_response_time:average',
  SELECTED_COUNT = 'selected_count',
  SELECTED_RATE = 'selected_rate',
  SERVICE_REFUSALS_COUNT = 'service_refusals:count',
  TIME_TO_FACILITY_DECISION_SECONDS_AVERAGE = 'time_to_facility_decision_seconds:average',
}

export enum Dimensions {
  DATE = 'date',
  DENIAL_REASON = 'denial_reason',
  EPISODE_CLASSIFICATION = 'episode_classification',
  EPISODE_ID = 'episode_id',
  EPISODE_URL = 'episode_url',
  GROUP = 'group',
  GROUP_NAME = 'group_name',
  HEALTH_PLAN_CLASSIFICATION = 'health_plan_classification',
  HOSPITAL = 'hospital',
  HOSPITAL_CLIENT = 'hospital_client',
  LENGTH_OF_STAY = 'length_of_stay',
  OWNER_CLIENT = 'owner_client',
  OWNER_GROUP_NAME = 'owner_group_name',
  PATIENT_NAME = 'patient_name',
  PLAN_TYPE_CLASSIFICATION = 'plan_type_classification',
  PREDECESSOR_LOCATION = 'predecessor_location',
  PROVIDER_CLIENT = 'provider_client',
  PROVIDER_ID = 'provider_id',
  PROVIDER_NAME = 'provider_name',
  READMISSION_DATE = 'readmission_date',
  REASON = 'reason',
  RECEIVING_CLIENT = 'receiving_client',
  RECEIVING_GROUP = 'receiving_group',
  REFERRAL_STATUS = 'referral_status',
  REFERRAL_URL = 'referral_url',
  FACILITY = 'facility',
  FACILITY_SELECTED = 'facility_selected',
  REFERRAL_SOURCE = 'referral_source',
  PAYER_SOURCE = 'payer_source',
  RESPONSE_TIME_SECONDS = 'response_time_seconds',
  REVIEW_ID = 'review_id',
  SENDING_LOCATION = 'sending_location',
  SERVICE_REFUSAL_REFUSED_BY_TYPE = 'service_refusal_refused_by_type',
}

// Mirrors Review::Referral::DENIAL_REASONS on the API.
export const DENIAL_REASON_DISPLAY_NAMES: Record<string, string> = {
  plan_type: 'Plan type not accepted',
  staffing_shortages: 'Staffing shortages',
  capacity_constraints: 'Capacity constraints',
  patient_acuity_too_high: 'Patient acuity too high',
  medical_necessity_not_met: 'Medical necessity not met',
  patient_location: 'Patient location',
  other: 'Other',
};

export enum Sources {
  ESCALATION_ACKS = 'escalation_acks',
  INSIGHTS_CARE_TRANSITIONS = 'insights_care_transitions',
  INSIGHTS_REFERRALS = 'insights_referrals',
  LOCATION_EPISODE_DAYS = 'location_episode_days',
  LOCATION_EPISODE_DISCHARGES = 'location_episode_discharges',
  REFERRAL_INTAKES = 'referral_intakes',
  REVIEWS = 'reviews',
}

export enum AccessModes {
  MANAGER = 'manager',
}

export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export const WITH_ID_SUFFIX = ':with_id';
